import { DateTime } from 'luxon';

/**
 * The periods people actually ask for, plus an escape hatch.
 *
 * Presets are calendar-aligned rather than rolling ("this month" is the 1st to today, not the last
 * 30 days) because that is what a supervisor comparing against a target or a previous month means.
 * `custom` carries no range of its own — it hands over to the date picker.
 */
export type DateRangePresetKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'last6Months'
  | 'thisYear'
  | 'lastYear'
  | 'allTime'
  | 'custom';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
  /** Absent on `custom`, which takes its range from the picker. */
  range?: (now: DateTime) => DateRange;
}

/**
 * The instants bounding a calendar-day selection locally. Datepickers hand back midnight on both
 * ends, so the end is stretched to 23:59:59.999 or the whole last day filters itself out.
 */
export function toWholeLocalDayRange(startDate: Date, endDate: Date): DateRange {
  return {
    startDate: DateTime.fromJSDate(startDate).startOf('day').toJSDate(),
    endDate: DateTime.fromJSDate(endDate).endOf('day').toJSDate(),
  };
}

/** Start of "all time". Nothing in the product predates it, so it reads as unbounded in practice. */
const EPOCH = DateTime.fromISO('1970-01-01T00:00:00');

/** A period that ends today rather than in the future — an end date past now reads as broken. */
function toDate(start: DateTime, end: DateTime, now: DateTime): DateRange {
  const cappedEnd = end > now ? now : end;
  return { startDate: start.startOf('day').toJSDate(), endDate: cappedEnd.endOf('day').toJSDate() };
}

/**
 * In display order, shortest period first; `custom` sits last. Order also decides ties in
 * `presetForDateRange` — on a Monday "this week" spans exactly today, and the narrower label wins.
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { key: 'today', label: 'Today', range: (now) => toDate(now, now, now) },
  {
    key: 'yesterday',
    label: 'Yesterday',
    range: (now) => {
      const yesterday = now.minus({ days: 1 });
      return toDate(yesterday, yesterday, now);
    },
  },
  { key: 'thisWeek', label: 'This week', range: (now) => toDate(now.startOf('week'), now.endOf('week'), now) },
  {
    key: 'lastWeek',
    label: 'Last week',
    range: (now) => {
      const lastWeek = now.minus({ weeks: 1 });
      return toDate(lastWeek.startOf('week'), lastWeek.endOf('week'), now);
    },
  },
  { key: 'thisMonth', label: 'This month', range: (now) => toDate(now.startOf('month'), now.endOf('month'), now) },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: (now) => {
      const lastMonth = now.minus({ months: 1 });
      return toDate(lastMonth.startOf('month'), lastMonth.endOf('month'), now);
    },
  },
  {
    // Rolling, not snapped to a month boundary: starting at the 1st made this identical to "this
    // year" for the whole of July, and the two became indistinguishable in the dropdown.
    key: 'last6Months',
    label: 'Last 6 months',
    range: (now) => toDate(now.minus({ months: 6 }), now, now),
  },
  { key: 'thisYear', label: 'This year', range: (now) => toDate(now.startOf('year'), now.endOf('year'), now) },
  {
    key: 'lastYear',
    label: 'Last year',
    range: (now) => {
      const lastYear = now.minus({ years: 1 });
      return toDate(lastYear.startOf('year'), lastYear.endOf('year'), now);
    },
  },
  {
    // A real start date, not an open-ended one: every consumer filters on a range.
    key: 'allTime',
    label: 'All time',
    range: (now) => toDate(EPOCH, now, now),
  },
  { key: 'custom', label: 'Custom' },
];

/** The period shown when nothing has been chosen — the same on every page that carries the control. */
export const DEFAULT_DATE_RANGE_PRESET: DateRangePresetKey = 'thisMonth';

export function dateRangeForPreset(key: DateRangePresetKey, now: Date = new Date()): DateRange | null {
  return DATE_RANGE_PRESETS.find((preset) => preset.key === key)?.range?.(DateTime.fromJSDate(now)) ?? null;
}

/**
 * Which preset a range corresponds to, or `custom` when it matches none.
 *
 * Derived rather than stored alongside the dates: the range lives in the URL, so a link shared from
 * "This month" would otherwise come back reading "Custom" — and a preset persisted next to dates it
 * no longer matches is a lie waiting to happen.
 *
 * Compared by calendar day, since the stored range is day-precision while the presets carry times.
 */
export function presetForDateRange(range: DateRange, now: Date = new Date()): DateRangePresetKey {
  const sameDay = (a: Date, b: Date) => DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'day');
  const match = DATE_RANGE_PRESETS.find((preset) => {
    const candidate = preset.range?.(DateTime.fromJSDate(now));
    return !!candidate && sameDay(candidate.startDate, range.startDate) && sameDay(candidate.endDate, range.endDate);
  });
  return match?.key ?? 'custom';
}
